import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Snackbar, Alert, CircularProgress } from "@mui/material";

const IdentifySpeakers = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [timestamps, setTimestamps] = useState([]);
  const [videoSrc, setVideoSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTimestampIndex, setCurrentTimestampIndex] = useState(0);
  const [speakerName, setSpeakerName] = useState("");
  const [speakerState, setSpeakerState] = useState({});
  const [usedNames, setUsedNames] = useState(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const videoRef = useRef(null);

  // Function to show snackbar
  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const fetchVideoSource = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URI}/api/unlabeled-video/${uuid}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch video source");
        }
        const videoBlob = await response.blob();
        const videoURL = URL.createObjectURL(videoBlob);
        setVideoSrc(videoURL);
      } catch (error) {
        console.error("Error fetching video source:", error);
        showSnackbar("Failed to load video.", "error");
      } finally {
        setIsLoading(false);
      }
    };

    if (!videoSrc && uuid) {
      fetchVideoSource();
    }
  }, [videoSrc, uuid]);

  const fetchTimestamps = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URI}/api/unlabeled-video/${uuid}/timestamps`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch timestamps JSON");
      }
      const timestampsJSON = await response.json();
      setTimestamps(timestampsJSON);
    } catch (error) {
      console.error("Error fetching timestamps:", error);
      showSnackbar("Failed to fetch timestamps.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!timestamps.length && uuid) {
      fetchTimestamps();
    }
  }, [timestamps.length, uuid]);

  useEffect(() => {
    if (timestamps.length > 0 && videoRef.current) {
      videoRef.current.currentTime = timestamps[currentTimestampIndex].time;
    }
  }, [currentTimestampIndex, timestamps]);

  const saveSpeakerName = (newName) => {
    const speakerNameString = newName ? newName : speakerName;
    if (speakerNameString.trim()) {
      const currentTime = videoRef.current.currentTime;
      const recentTimestamp = findMostRecentTimestamp(currentTime);
      if (recentTimestamp) {
        setSpeakerState((prev) => ({
          ...prev,
          [recentTimestamp.time]: speakerNameString.trim(),
        }));
        setUsedNames((prev) => new Set(prev).add(speakerNameString.trim()));
        setSpeakerName("");
        moveToNextTimestamp();
        showSnackbar("Speaker name saved.", "success");
      }
    } else {
      showSnackbar("Please enter speaker name.", "warning");
    }
  };

  const refresh = () => {
    window.location.href = window.location.href;
  };

  const findMostRecentTimestamp = (currentTime) => {
    for (let i = timestamps.length - 1; i >= 0; i--) {
      if (timestamps[i].time <= currentTime) {
        return timestamps[i];
      }
    }
    return null;
  };

  const moveToNextTimestamp = () => {
    if (currentTimestampIndex < timestamps.length - 1) {
      setCurrentTimestampIndex(currentTimestampIndex + 1);
    }
  };

  const skipChunk = () => {
    moveToNextTimestamp();
    showSnackbar("Skipped timestamp.", "info");
  };

  const rewind = () => {
    if (currentTimestampIndex > 0) {
      setCurrentTimestampIndex(currentTimestampIndex - 1);
      showSnackbar("Rewinded to the previous timestamp.", "info");
    }
  };

  const fastForward = () => {
    if (currentTimestampIndex < timestamps.length - 1) {
      setCurrentTimestampIndex(currentTimestampIndex + 1);
      showSnackbar("Fast-forwarded to the next timestamp.", "info");
    }
  };

  const saveToFile = async () => {
    if (Object.keys(speakerState).length === 0) {
      showSnackbar(
        "Error! At least one speaker needs to be identified.",
        "error"
      );
      return;
    }

    try {
      setIsUploading(true);

      const jsonContent = JSON.stringify(speakerState, null, 2);

      const response = await fetch(
        `${process.env.REACT_APP_API_URI}/api/unlabeled-video/${uuid}/speakers/save`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: jsonContent,
        }
      );

      if (response.ok) {
        showSnackbar("Names saved to Google Cloud Storage.", "success");
        navigate("/?showThankYouText=true");
      } else {
        showSnackbar("Error saving to Google Cloud Storage.", "error");
      }
    } catch (error) {
      console.error("Error saving to file:", error);
      showSnackbar("Error saving to Google Cloud Storage.", "error");
    }
  };

  if (isLoading) {
    return <div className="loading-spinner">Loading video...</div>;
  }

  if (!videoSrc) {
    return <div className="error-message">Failed to load video.</div>;
  }

  return (
    <div id="video-container">
      <Alert severity="info">
        Instructions: In order for our database to know the names of the person
        being interviewed, it's necessary for you to label all people who are
        part of the video. Please enter the name of the person speaking in the
        video at the current time, and then click next.
        <br />
        <br />
        Click "Skip" if you are unable to identify the speaker, or if there are
        no speakers.
        <br />
        <br />
        If you make a mistake, you can click "Start Over" to begin again.
        <br />
        <br />
        Once you have finished identifying all speakers, click "Save" to save
        the names to the database.
      </Alert>
      <br />
      <br />
      {isUploading ? (
        <CircularProgress />
      ) : (
        <>
          <video id="video" controls ref={videoRef}>
            <source src={videoSrc} type="video/mp4" />
          </video>

          <div id="overlay">
            {currentTimestampIndex >= timestamps.length - 1 &&
            Object.keys(speakerState).length > 0 ? (
              <div>
                <h3>Speaker identification complete!</h3>
                <Button
                  variant="contained"
                  color="success"
                  onClick={saveToFile}
                >
                  Save
                </Button>
              </div>
            ) : (
              <>
                <div
                  id="used-names"
                  style={{ display: usedNames.size > 0 ? "block" : "none" }}
                >
                  <h3>Previously Used Names:</h3>
                  {[...usedNames].map((name) => (
                    <Button
                      key={name}
                      variant="outlined"
                      onClick={() => {
                        setSpeakerName(name);
                        saveSpeakerName(name);
                      }}
                    >
                      {name}
                    </Button>
                  ))}
                </div>
                <label htmlFor="speaker-name">Name of person speaking:</label>
                <br />
                <input
                  type="text"
                  id="speaker-name"
                  placeholder="Enter name"
                  value={speakerName}
                  onChange={(e) => setSpeakerName(e.target.value)}
                />
                <br />
                <br />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => saveSpeakerName()}
                >
                  Next
                </Button>
                &nbsp;
                <Button variant="outlined" onClick={skipChunk}>
                  Skip
                </Button>
                <br />
                <br />
                <Button
                  variant="contained"
                  color="success"
                  onClick={saveToFile}
                >
                  Finish
                </Button>
                &nbsp;
                <Button variant="outlined" onClick={refresh}>
                  Start Over
                </Button>
              </>
            )}
          </div>
          <div id="controls">
            <Button variant="contained" color="primary" onClick={rewind}>
              Rewind
            </Button>
            &nbsp;
            <Button variant="contained" color="primary" onClick={fastForward}>
              Fast forward
            </Button>
          </div>
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default IdentifySpeakers;
